
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Country, CountryContextType } from './types';
import { Constituency } from '@/types/geography';
import { useCountryFetching } from './useCountryFetching';
import { useConstituencyFetching } from './useConstituencyFetching';
import { SocialPlatform } from '@/types/socialPlatform';

// Create the context with a default value
const CountryContext = createContext<CountryContextType>({
  countryCode: null,
  countryName: null,
  selectedCountry: null,
  availableCountries: [],
  availableConstituencies: [],
  isLoading: true,
  setCountry: () => {},
  refreshCountries: async () => {},
});

// Hook to use the country context
export const useCountry = () => useContext(CountryContext);

// Provider component
export const CountryProvider = ({ children }: { children: ReactNode }) => {
  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [countryName, setCountryName] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Use the hooks for fetching countries and constituencies
  const { availableCountries, fetchAvailableCountries } = useCountryFetching();
  const { availableConstituencies, fetchConstituencies } = useConstituencyFetching();

  // Function to set the active country
  const setCountry = (code: string) => {
    setCountryCode(code);
    const country = availableCountries.find(c => c.code === code);
    if (country) {
      setCountryName(country.name);
      setSelectedCountry(country as Country);
    }
  };

  // Function to refresh countries data
  const refreshCountries = async () => {
    setIsLoading(true);
    try {
      const countries = await fetchAvailableCountries();
      await fetchConstituencies();
      
      // If there's a current countryCode, refresh the selected country info
      if (countryCode) {
        const country = countries.find(c => c.code === countryCode);
        if (country) {
          setCountryName(country.name);
          setSelectedCountry(country as Country);
        }
      }
    } catch (error) {
      console.error("Error refreshing countries:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    refreshCountries();
  }, []);

  // Context value
  const contextValue = {
    countryCode,
    countryName,
    selectedCountry,
    availableCountries,
    availableConstituencies,
    isLoading,
    setCountry,
    refreshCountries
  };

  return (
    <CountryContext.Provider value={contextValue}>
      {children}
    </CountryContext.Provider>
  );
};
